<template>
	<v-card class="ma-5" flat :loading="loading">
		<v-card-text class="pa-0">
			<div class="panel-content px-4 py-4">
				<!-- Current Organization Agent Section -->
				<div class="mb-4">
					<div class="d-flex align-center mb-2">
						<span class="font-weight-bold">{{ $t("current-organization") }}</span>
					</div>

					<v-alert
						v-if="!currentOrgAgents.length && !loading"
						type="info"
						outlined
						dense
					>
						{{ $t("no-current-agent") }}
					</v-alert>

					<v-list v-else-if="currentOrgAgents.length" class="pa-0">
						<v-list-item
							v-for="currentOrgAgent in currentOrgAgents"
							:key="currentOrgAgent.id"
							class="agent-item current-org-agent mb-2"
						>
							<v-list-item-content>
								<v-list-item-title class="d-flex align-center">
									<span class="font-weight-medium mr-2">
										{{ currentOrgAgent?.organizationName["fr-FR"] }}
									</span>
									<v-chip x-small color="info" class="mr-2">
										{{ $t("current") }}
									</v-chip>
									<v-spacer />
									<!-- Action menu -->
									<v-menu offset-y left>
										<template #activator="{ on, attrs }">
											<v-btn icon x-small v-bind="attrs" v-on="on">
												<v-icon small>mdi-dots-vertical</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item
												:disabled="loading || resettingId !== null || downloadingInstaller || disablingId !== null"
												@click="openDownloadInstallerDialog(currentOrgAgent)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="blue">mdi-download</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("download-latest-agent") }}</v-list-item-title>
											</v-list-item>
											<v-list-item
												:disabled="loading || resettingId !== null || downloadingInstaller || disablingId !== null"
												@click="confirmResetLastTransfer(currentOrgAgent)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="warning">mdi-history</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("reset") }} lastTransfer</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-if="currentOrgAgent.enabled"
												:disabled="loading || resettingId !== null || downloadingInstaller || disablingId !== null"
												@click="confirmDisableAgent(currentOrgAgent)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="error">mdi-power</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("disable-agent") }}</v-list-item-title>
											</v-list-item>
											<v-list-item
												v-else
												:disabled="loading || resettingId !== null || downloadingInstaller || disablingId !== null"
												@click="confirmEnableAgent(currentOrgAgent)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="success">mdi-power</v-icon>
												</v-list-item-icon>
												<v-list-item-title>{{ $t("enable-agent") }}</v-list-item-title>
											</v-list-item>
											<v-divider />
											<v-list-item
												:disabled="loading || resettingId !== null || downloadingInstaller || disablingId !== null"
												@click="confirmRemovePatientAgentIntegration(currentOrgAgent)"
											>
												<v-list-item-icon class="mr-2">
													<v-icon small color="error">mdi-delete</v-icon>
												</v-list-item-icon>
												<v-list-item-title class="error--text">
													{{ $t("remove-patient-agent-integration") }}
												</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
								</v-list-item-title>
								<v-list-item-title class="d-flex align-center">
									<v-chip
										:color="currentOrgAgent.enabled ? 'primary' : 'error'"
										x-small
										class="mr-2"
									>
										{{ currentOrgAgent.enabled ? $t("enabled") : $t("disabled") }}
									</v-chip>
									<v-chip color="secondary" x-small class="mr-2">
										{{ currentOrgAgent.erpLabel || currentOrgAgent.erpType }}
									</v-chip>
								</v-list-item-title>
								<v-list-item-subtitle class="mt-1">
									<div class="d-flex flex-column">
										<span class="text-caption grey--text">
											<strong>{{ $t("organization-id") }}:</strong> {{ currentOrgAgent.organizationId }}
										</span>
										<span class="text-caption grey--text">
											<strong>{{ $t("agent-id") }}:</strong> {{ currentOrgAgent.id }}
											<v-icon
												small
												class="ml-1 copy-icon"
												@click.stop="copyToClipboard(currentOrgAgent.id)"
											>mdi-content-copy</v-icon>
										</span>
										<span class="text-caption grey--text">
											<strong>{{ $t("last-transfer") }}:</strong>
											{{ currentOrgAgent.lastTransfer ? formatDate(currentOrgAgent.lastTransfer) : $t("never") }}
											<v-icon
												v-if="isTransferStale(currentOrgAgent.lastTransfer)"
												small
												color="warning"
												class="ml-1"
											>mdi-alert</v-icon>
										</span>
										<span v-if="currentOrgAgent.nextTransferAt" class="text-caption grey--text">
											<strong>{{ $t("next-transfer") }}:</strong>
											{{ formatDate(currentOrgAgent.nextTransferAt) }}
											<v-tooltip v-if="isNextTransferOverdue(currentOrgAgent)" bottom>
												<template #activator="{ on, attrs }">
													<v-icon
														small
														color="warning"
														class="ml-1"
														v-bind="attrs"
														v-on="on"
													>mdi-alert</v-icon>
												</template>
												<span>{{ $t("next-transfer-overdue") }}</span>
											</v-tooltip>
											<span
												v-if="isNextTransferOverdue(currentOrgAgent)"
												class="d-sr-only"
											>{{ $t("next-transfer-overdue") }}</span>
										</span>
										<span v-if="currentOrgAgent.lastError" class="text-caption error--text agent-error">
											<v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
											<strong>{{ $t("last-error") }}:</strong> {{ currentOrgAgent.lastError }}
											<template v-if="currentOrgAgent.lastErrorAt">
												({{ formatDate(currentOrgAgent.lastErrorAt) }})
											</template>
										</span>
										<span v-if="currentOrgAgent.lastConnection" class="text-caption grey--text">
											<strong>{{ $t("last-connection") }}:</strong>
											{{ formatDate(currentOrgAgent.lastConnection) }}
										</span>
										<span v-if="currentOrgAgent.version" class="text-caption grey--text">
											<strong>{{ $t("version", { version: currentOrgAgent.version }) }}</strong>
											<template v-if="currentOrgAgent.versionUpdatedAt">
												({{ $t("version-updated-at", { date: formatDate(currentOrgAgent.versionUpdatedAt) }) }})
											</template>
										</span>
										<span class="text-caption grey--text d-flex align-center">
											<strong>{{ $t("query") }}:</strong>
											<v-btn
												icon
												x-small
												class="ml-1"
												:loading="queryLoadingId === currentOrgAgent.id"
												@click.stop="toggleQuery(currentOrgAgent)"
											>
												<v-icon small color="primary">
													{{ expandedQueryId === currentOrgAgent.id ? 'mdi-eye-off' : 'mdi-eye' }}
												</v-icon>
											</v-btn>
											<v-icon
												v-if="expandedQueryId === currentOrgAgent.id"
												small
												class="ml-1 copy-icon"
												@click.stop="copyToClipboard(agentQueries[currentOrgAgent.id]?.query || '')"
											>mdi-content-copy</v-icon>
										</span>
									</div>
								</v-list-item-subtitle>
								<div v-if="expandedQueryId === currentOrgAgent.id" class="query-section mt-2">
									<pre class="query-content text-caption">{{ formatQuery(agentQueries[currentOrgAgent.id]?.query) }}</pre>
								</div>
							</v-list-item-content>
						</v-list-item>
					</v-list>
				</div>

				<v-divider class="mb-4" />

				<!-- Other Agents Section -->
				<div>
					<div
						class="d-flex align-center mb-2 other-agents-header"
						@click="toggleOtherAgents"
					>
						<v-icon small class="mr-2">
							{{ showOtherAgents ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
						</v-icon>
						<span class="font-weight-bold">{{ $t("other-agents") }}</span>
						<v-chip x-small class="ml-2" color="grey lighten-2">
							{{ otherAgents.length }}
						</v-chip>
						<v-progress-circular
							v-if="loadingOtherAgents"
							indeterminate
							size="16"
							width="2"
							class="ml-2"
						/>
						<v-spacer />

						<!-- Filter controls (only show when expanded) -->
						<template v-if="showOtherAgents && otherAgents.length > 0">
							<!-- Results count -->
							<span class="text-caption grey--text mr-2">
								{{ filteredOtherAgents.length }} / {{ otherAgents.length }}
							</span>

							<!-- Active filter chips -->
							<v-chip
								v-if="showOnlyStale"
								small
								close
								color="primary"
								class="mr-2"
								@click.stop
								@click:close="showOnlyStale = false"
							>
								7+ {{ $t("days") }}
							</v-chip>
							<v-chip
								v-if="showOnlyDisabled"
								small
								close
								color="primary"
								class="mr-2"
								@click.stop
								@click:close="showOnlyDisabled = false"
							>
								{{ $t("disabled") }}
							</v-chip>
							<v-chip
								v-for="erpType in selectedErpTypes"
								:key="erpType"
								small
								close
								color="primary"
								class="mr-2 text-capitalize"
								@click.stop
								@click:close="toggleErpType(erpType)"
							>
								{{ erpType }}
							</v-chip>

							<v-menu offset-y :close-on-content-click="false">
								<template #activator="{ on, attrs }">
									<v-badge
										:value="activeFilterCount > 0"
										:content="activeFilterCount"
										color="primary"
										overlap
									>
										<v-btn
											icon
											small
											v-bind="attrs"
											v-on="on"
											@click.stop
										>
											<v-icon>mdi-filter</v-icon>
										</v-btn>
									</v-badge>
								</template>
								<v-list dense>
									<v-list-item @click="showOnlyStale = !showOnlyStale">
										<v-list-item-action class="mr-2">
											<v-checkbox
												v-model="showOnlyStale"
												hide-details
												dense
											/>
										</v-list-item-action>
										<v-list-item-title>{{ $t("filter-stale-transfers") }}</v-list-item-title>
									</v-list-item>
									<v-list-item @click="showOnlyDisabled = !showOnlyDisabled">
										<v-list-item-action class="mr-2">
											<v-checkbox
												v-model="showOnlyDisabled"
												hide-details
												dense
											/>
										</v-list-item-action>
										<v-list-item-title>{{ $t("filter-disabled") }}</v-list-item-title>
									</v-list-item>
									<v-divider class="my-1" />
									<v-subheader>{{ $t("erp-type") }}</v-subheader>
									<v-list-item
										v-for="erpType in erpTypes"
										:key="erpType"
										@click="toggleErpType(erpType)"
									>
										<v-list-item-action class="mr-2">
											<v-checkbox
												:input-value="selectedErpTypes.includes(erpType)"
												hide-details
												dense
											/>
										</v-list-item-action>
										<v-list-item-title class="text-capitalize">
											{{ erpType }}
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</div>

					<template v-if="showOtherAgents">
						<v-alert
							v-if="!filteredOtherAgents.length && !loadingOtherAgents"
							type="info"
							outlined
							dense
							class="mb-4"
						>
							{{ $t("no-agents-found") }}
						</v-alert>

						<v-list v-else class="pa-0">
							<div v-for="(agent, index) in filteredOtherAgents" :key="`${agent.id}-${index}`">
								<v-list-item class="agent-item mb-2">
									<v-list-item-content>
										<v-list-item-title class="d-flex align-center">
											<span class="font-weight-medium mr-2">
												{{ agent?.organizationName["fr-FR"] }}
											</span>
											<v-spacer />
											<v-menu offset-y left>
												<template #activator="{ on, attrs }">
													<v-btn icon x-small v-bind="attrs" v-on="on">
														<v-icon small>mdi-dots-vertical</v-icon>
													</v-btn>
												</template>
												<v-list dense>
													<v-list-item
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="confirmResetLastTransfer(agent)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="warning">mdi-history</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("reset") }} lastTransfer</v-list-item-title>
													</v-list-item>
													<v-list-item
														v-if="agent.enabled"
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="confirmDisableAgent(agent)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="error">mdi-power</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("disable-agent") }}</v-list-item-title>
													</v-list-item>
													<v-list-item
														v-else
														:disabled="loading || resettingId !== null || disablingId !== null"
														@click="confirmEnableAgent(agent)"
													>
														<v-list-item-icon class="mr-2">
															<v-icon small color="success">mdi-power</v-icon>
														</v-list-item-icon>
														<v-list-item-title>{{ $t("enable-agent") }}</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</v-list-item-title>
										<v-list-item-title class="d-flex align-center">
											<v-chip
												:color="agent.enabled ? 'primary' : 'error'"
												x-small
												class="mr-2"
											>
												{{ agent.enabled ? $t("enabled") : $t("disabled") }}
											</v-chip>
											<v-chip color="secondary" x-small class="mr-2">
												{{ agent.erpLabel || agent.erpType }}
											</v-chip>
										</v-list-item-title>
										<v-list-item-subtitle class="mt-1">
											<div class="d-flex flex-column">
												<span class="text-caption grey--text">
													<strong>{{ $t("organization-id") }}:</strong> {{ agent.organizationId }}
												</span>
												<span class="text-caption grey--text">
													<strong>{{ $t("agent-id") }}:</strong> {{ agent.id }}
													<v-icon
														small
														class="ml-1 copy-icon"
														@click.stop="copyToClipboard(agent.id)"
													>mdi-content-copy</v-icon>
												</span>
												<span class="text-caption grey--text">
													<strong>{{ $t("last-transfer") }}:</strong>
													{{ agent.lastTransfer ? formatDate(agent.lastTransfer) : $t("never") }}
													<v-icon
														v-if="isTransferStale(agent.lastTransfer)"
														small
														color="warning"
														class="ml-1"
													>mdi-alert</v-icon>
												</span>
												<span v-if="agent.nextTransferAt" class="text-caption grey--text">
													<strong>{{ $t("next-transfer") }}:</strong>
													{{ formatDate(agent.nextTransferAt) }}
													<v-tooltip v-if="isNextTransferOverdue(agent)" bottom>
														<template #activator="{ on, attrs }">
															<v-icon
																small
																color="warning"
																class="ml-1"
																v-bind="attrs"
																v-on="on"
															>mdi-alert</v-icon>
														</template>
														<span>{{ $t("next-transfer-overdue") }}</span>
													</v-tooltip>
													<span
														v-if="isNextTransferOverdue(agent)"
														class="d-sr-only"
													>{{ $t("next-transfer-overdue") }}</span>
												</span>
												<span v-if="agent.lastError" class="text-caption error--text agent-error">
													<v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
													<strong>{{ $t("last-error") }}:</strong> {{ agent.lastError }}
													<template v-if="agent.lastErrorAt">
														({{ formatDate(agent.lastErrorAt) }})
													</template>
												</span>
												<span v-if="agent.lastConnection" class="text-caption grey--text">
													<strong>{{ $t("last-connection") }}:</strong>
													{{ formatDate(agent.lastConnection) }}
												</span>
												<span v-if="agent.version" class="text-caption grey--text">
													<strong>{{ $t("version", { version: agent.version }) }}</strong>
													<template v-if="agent.versionUpdatedAt">
														({{ $t("version-updated-at", { date: formatDate(agent.versionUpdatedAt) }) }})
													</template>
												</span>
												<span class="text-caption grey--text d-flex align-center">
													<strong>{{ $t("query") }}:</strong>
													<v-btn
														icon
														x-small
														class="ml-1"
														:loading="queryLoadingId === agent.id"
														@click.stop="toggleQuery(agent)"
													>
														<v-icon small color="primary">
															{{ expandedQueryId === agent.id ? 'mdi-eye-off' : 'mdi-eye' }}
														</v-icon>
													</v-btn>
													<v-icon
														v-if="expandedQueryId === agent.id"
														small
														class="ml-1 copy-icon"
														@click.stop="copyToClipboard(agentQueries[agent.id]?.query || '')"
													>mdi-content-copy</v-icon>
												</span>
											</div>
										</v-list-item-subtitle>
										<div v-if="expandedQueryId === agent.id" class="query-section mt-2">
											<pre class="query-content text-caption">{{ formatQuery(agentQueries[agent.id]?.query) }}</pre>
										</div>
									</v-list-item-content>
								</v-list-item>
								<v-divider class="my-1" />
							</div>
						</v-list>
					</template>
				</div>
			</div>
		</v-card-text>

		<confirmation-dialog ref="dialogRef" content-class="devops-subdialog" />

		<!-- Download Installer Dialog -->
		<v-dialog v-model="downloadInstallerDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('download-latest-agent') }}
					<v-spacer />
					<v-btn icon small :disabled="downloadingInstaller" @click="closeDownloadInstallerDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-radio-group
						v-model="downloadInstallerMode"
						:disabled="downloadingInstaller"
						dense
						hide-details
						class="mt-0"
					>
						<v-radio :label="$t('agent-install-new')" value="new" />
						<v-radio :label="$t('agent-install-repair')" value="repair" :disabled="!downloadInstallerCanRepair" />
					</v-radio-group>
					<v-alert
						v-if="!downloadInstallerCanRepair"
						type="warning"
						text
						dense
						class="mt-3 mb-0"
					>
						{{ $t('agent-install-repair-unavailable') }}
					</v-alert>
					<div class="text-caption grey--text mt-2">
						{{ downloadInstallerMode === 'new' ? $t('agent-install-new-hint') : $t('agent-install-repair-hint') }}
					</div>
					<v-checkbox
						v-if="downloadInstallerMode === 'new'"
						v-model="downloadInstallerResetLastTransfer"
						:label="$t('agent-reset-last-transfer')"
						:disabled="downloadingInstaller"
						dense
						hide-details
						class="mt-4"
					/>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn color="error" class="px-4" :disabled="downloadingInstaller" @click="closeDownloadInstallerDialog">
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:loading="downloadingInstaller"
						class="px-4"
						@click="downloadAgentInstaller"
					>
						{{ $t('continue') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Agent License Key Dialog -->
		<v-dialog v-model="licenseKeyDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('agent-license-key') }}
					<v-spacer />
					<v-btn icon small @click="licenseKeyDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-alert type="info" text dense class="mb-4">
						{{ $t('agent-license-key-info') }}
					</v-alert>
					<v-text-field
						:value="agentLicenseAgentId"
						:label="$t('agent-id')"
						outlined
						dense
						readonly
						append-icon="mdi-content-copy"
						@click:append="copyToClipboard(agentLicenseAgentId)"
					/>
					<v-text-field
						:value="agentLicenseKey"
						:label="$t('license-key')"
						outlined
						dense
						readonly
						append-icon="mdi-content-copy"
						@click:append="copyToClipboard(agentLicenseKey)"
					/>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-end">
					<v-btn color="primary" class="px-4" @click="licenseKeyDialog = false">
						{{ $t('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import ConfirmationDialog from "@/components/configuration/ConfirmationDialog.vue";

export default {
	name: "AgentPanel",

	components: {
		ConfirmationDialog
	},

	data() {
		return {
			currentOrgAgents: [],
			otherAgents: [],
			loading: false,
			loadingOtherAgents: false,
			showOtherAgents: false,
			otherAgentsFetched: false,
			resettingId: null,
			disablingId: null,
			downloadingInstaller: false,
			downloadInstallerDialog: false,
			downloadInstallerAgentId: "",
			downloadInstallerResetLastTransfer: false,
			downloadInstallerMode: "new",
			downloadInstallerCanRepair: false,
			licenseKeyDialog: false,
			agentLicenseAgentId: "",
			agentLicenseKey: "",
			queryLoadingId: null,
			expandedQueryId: null,
			agentQueries: {},
			showOnlyStale: false,
			showOnlyDisabled: false,
			selectedErpTypes: [],
			erpTypes: []
		};
	},

	computed: {
		filteredOtherAgents() {
			let result = this.otherAgents;

			if (this.showOnlyStale) {
				result = result.filter(agent => this.isTransferStale(agent.lastTransfer));
			}
			if (this.showOnlyDisabled) {
				result = result.filter(agent => !agent.enabled);
			}
			if (this.selectedErpTypes.length > 0) {
				result = result.filter(agent =>
					this.selectedErpTypes.includes(agent.erpType?.toLowerCase())
				);
			}

			return result;
		},

		activeFilterCount() {
			let count = 0;

			if (this.showOnlyStale) count++;
			if (this.showOnlyDisabled) count++;
			if (this.selectedErpTypes.length > 0) count++;
			return count;
		}
	},

	async created() {
		await Promise.all([
			this.fetchCurrentOrgAgent(),
			this.fetchErpTypes(),
			this.fetchOtherAgents()
		]);
	},

	methods: {
		async fetchAgents(options) {
			const silent = options?.silent || false;

			if (!silent) this.otherAgentsFetched = false;
			const results = await Promise.allSettled([
				this.fetchCurrentOrgAgent({ silent }),
				this.fetchOtherAgents({ silent })
			]);

			const hasError = results.some(r =>
				r.status === "rejected" || (r.status === "fulfilled" && r.value?.success === false)
			);

			return { success: !hasError };
		},

		async fetchErpTypes() {
			try {
				const { data } = await this.axios.get("/devops/erp-types");

				this.erpTypes = data;
			} catch (err) {
				console.error("Error fetching ERP types:", err);
			}
		},

		toggleErpType(erpType) {
			const index = this.selectedErpTypes.indexOf(erpType);

			if (index === -1) {
				this.selectedErpTypes.push(erpType);
			} else {
				this.selectedErpTypes.splice(index, 1);
			}
		},

		async fetchCurrentOrgAgent(options) {
			const silent = options?.silent || false;

			if (!silent) this.loading = true;
			try {
				const { data } = await this.axios.get("/devops/agents/current");

				this.currentOrgAgents = data;
				return { success: true };
			} catch (err) {
				console.error("Error fetching current org agent:", err);
				if (!silent) this.$error(this.$t("error.fetch-agents"));
				return { success: false };
			} finally {
				if (!silent) this.loading = false;
			}
		},

		async fetchOtherAgents(options) {
			const silent = options?.silent || false;

			if (!silent && this.otherAgentsFetched) return { success: true };

			if (!silent) this.loadingOtherAgents = true;
			try {
				const { data } = await this.axios.get("/devops/agents/others");

				this.otherAgents = data;
				this.otherAgentsFetched = true;
				return { success: true };
			} catch (err) {
				console.error("Error fetching other agents:", err);
				if (!silent) this.$error(this.$t("error.fetch-agents"));
				return { success: false };
			} finally {
				if (!silent) this.loadingOtherAgents = false;
			}
		},

		async toggleOtherAgents() {
			this.showOtherAgents = !this.showOtherAgents;

			if (this.showOtherAgents && !this.otherAgentsFetched) {
				await this.fetchOtherAgents();
			}
		},

		async copyToClipboard(text) {
			try {
				const textToCopy = typeof text === "object" ? JSON.stringify(text, null, 2) : text;

				await navigator.clipboard.writeText(textToCopy);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (error) {
				this.$error(this.$t("error.copy-failed"));
			}
		},

		formatQuery(query) {
			if (!query) return this.$t("no-query-available");
			if (typeof query === "object") return JSON.stringify(query, null, 2);
			return query;
		},

		async toggleQuery(agent) {
			if (this.expandedQueryId === agent.id) {
				this.expandedQueryId = null;
				return;
			}

			// If we haven't fetched this agent's query yet, fetch it
			if (!this.agentQueries[agent.id]) {
				this.queryLoadingId = agent.id;
				try {
					const { data } = await this.axios.get(`/patients/integrations/devops/agent/${agent.id}`);

					this.$set(this.agentQueries, agent.id, {
						query: data.agent?.query || null
					});
				} catch (err) {
					console.error("Error fetching agent query:", err);
					this.$error(this.$t("error.fetch-agent-query"));
					this.$set(this.agentQueries, agent.id, { query: null });
				} finally {
					this.queryLoadingId = null;
				}
			}

			this.expandedQueryId = agent.id;
		},

		openDownloadInstallerDialog(agent) {
			this.downloadInstallerAgentId = agent.id;
			this.downloadInstallerResetLastTransfer = false;
			// Repair only works if the backend still knows the installation (/reset clears installedOn)
			this.downloadInstallerCanRepair = Boolean(agent.installedOn);
			this.downloadInstallerMode = this.downloadInstallerCanRepair ? "repair" : "new";
			this.downloadInstallerDialog = true;
		},

		closeDownloadInstallerDialog() {
			this.downloadInstallerDialog = false;
			this.downloadInstallerAgentId = "";
		},

		async downloadAgentInstaller() {
			const agentId = this.downloadInstallerAgentId;

			this.downloadingInstaller = true;
			try {
				await this.downloadInstallerFile();

				// Repair keeps the existing registration: the installer restores the current keys
				if (this.downloadInstallerMode === "new") {
					// Reset only after a successful download: it invalidates the current installation
					const { data: licenseKey } = await this.axios.put(
						`/patients/integrations/admin/agents/reset/${encodeURIComponent(agentId)}`,
						{ resetLastTransfer: this.downloadInstallerResetLastTransfer }
					);

					this.agentLicenseAgentId = agentId;
					this.agentLicenseKey = licenseKey;
					this.licenseKeyDialog = true;
				}

				this.$success(this.$t("download-success"));
				this.closeDownloadInstallerDialog();
				await this.fetchCurrentOrgAgent({ silent: true });
			} catch (err) {
				console.error("Error downloading installer:", err);
				this.$error(err.response?.data?.message || this.$t("error.download-installer"));
			} finally {
				this.downloadingInstaller = false;
			}
		},

		async downloadInstallerFile() {
			const response = await this.axios.get(
				"/patients/integrations/admin/agents/release/installer",
				{ responseType: "blob" }
			);

			const blob = new Blob([response.data], { type: "application/octet-stream" });
			const link = document.createElement("a");
			const filename = /filename="?([^";]+)"?/.exec(response.headers["content-disposition"] ?? "")?.[1];

			link.href = URL.createObjectURL(blob);
			link.download = filename || "FreskAgent.msi";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(link.href);
		},

		async confirmResetLastTransfer(agent) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("reset-last-transfer-title"),
					body: this.$t("reset-last-transfer-confirm", { organization: agent.organizationName["fr-FR"] }),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			await this.resetLastTransfer(agent.id);
		},

		async resetLastTransfer(agentId) {
			this.resettingId = agentId;
			try {
				const { data } = await this.axios.post(`/devops/agents/${agentId}/reset-transfer`);

				this.updateAgentData(agentId, data);

				this.$success(this.$t("reset-last-transfer-success"));
			} catch (err) {
				console.error("Error resetting last transfer:", err);
				this.$error(this.$t("error.reset-last-transfer"));
			} finally {
				this.resettingId = null;
			}
		},

		async confirmRemovePatientAgentIntegration(agent) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("remove-patient-agent-integration"),
					body: this.$t("remove-patient-agent-integration-confirm", { organization: agent.organizationName["fr-FR"] }),
					confirm: this.$t("remove"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			this.loading = true;
			try {
				await this.axios.delete(`/patients/integrations/admin/agents/${encodeURIComponent(agent.id)}`);

				this.currentOrgAgents = this.currentOrgAgents.filter(current => current.id !== agent.id);
				if (this.expandedQueryId === agent.id) this.expandedQueryId = null;
				this.$delete(this.agentQueries, agent.id);
				this.$success(this.$t("remove-patient-agent-integration-success"));
			} catch (err) {
				console.error("Error removing patient agent integration:", err);
				this.$error(err.response?.data?.message || this.$t("error.remove-patient-agent-integration"));
			} finally {
				this.loading = false;
			}
		},

		async confirmDisableAgent(agent) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("disable-agent-title"),
					body: this.$t("disable-agent-confirm", { organization: agent.organizationName["fr-FR"] }),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			await this.disableAgent(agent.id);
		},

		async disableAgent(agentId) {
			this.disablingId = agentId;
			try {
				const { data } = await this.axios.patch(`/devops/agents/${agentId}/disable`);

				this.updateAgentData(agentId, data);

				this.$success(this.$t("disable-agent-success"));
			} catch (err) {
				console.error("Error disabling agent:", err);
				this.$error(this.$t("error.disable-agent"));
			} finally {
				this.disablingId = null;
			}
		},

		async confirmEnableAgent(agent) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("enable-agent-title"),
					body: this.$t("enable-agent-confirm", { organization: agent.organizationName["fr-FR"] }),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			await this.enableAgent(agent.id);
		},

		async enableAgent(agentId) {
			this.disablingId = agentId;
			try {
				const { data } = await this.axios.patch(`/devops/agents/${agentId}/enable`);

				this.updateAgentData(agentId, data);

				this.$success(this.$t("enable-agent-success"));
			} catch (err) {
				console.error("Error enabling agent:", err);
				this.$error(this.$t("error.enable-agent"));
			} finally {
				this.disablingId = null;
			}
		},

		updateAgentData(agentId, data) {
			// Check if it's the current org agent
			const currentIndex = this.currentOrgAgents.findIndex(agent => agent.id === agentId);

			if (currentIndex !== -1) {
				this.$set(this.currentOrgAgents, currentIndex, data);
				return;
			}

			// Otherwise update in other agents list
			const index = this.otherAgents.findIndex(a => a.id === agentId);

			if (index !== -1) {
				this.$set(this.otherAgents, index, data);
			}
		},

		formatDate(dateString) {
			if (!dateString) return this.$t("never");

			const date = new Date(dateString);

			return date.toLocaleString(this.$i18n.locale, {
				year: "numeric",
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit"
			});
		},

		isTransferStale(dateString) {
			if (!dateString) return true;

			const date = new Date(dateString);
			const now = new Date();
			const diffMs = now - date;
			const diffDays = diffMs / (1000 * 60 * 60 * 24);

			return diffDays >= 7;
		},

		isNextTransferOverdue(agent) {
			// Disabled agents legitimately never reach their next transfer
			if (!agent?.enabled || !agent?.nextTransferAt) return false;

			// 15 min grace: nextTransferAt is briefly in the past while a cycle runs
			const graceMs = 15 * 60 * 1000;

			return new Date() - new Date(agent.nextTransferAt) > graceMs;
		}
	}
};
</script>

<style scoped>
.panel-content {
	max-height: 750px;
	overflow-y: auto;
	overflow-x: hidden;
}

@media (max-width: 599px) {
	.panel-content {
		max-height: none;
	}
}

.agent-item {
	border-radius: 8px;
	background-color: #fafafa;
	height: auto !important;
	min-height: unset !important;
}

.agent-item.current-org-agent {
	background-color: #e3f2fd;
	border-left: 3px solid #1976d2;
}

.agent-item.current-org-agent:hover {
	background-color: #bbdefb;
}

.agent-item >>> .v-list-item__content {
	height: auto !important;
	min-height: unset !important;
	align-self: stretch !important;
}

.agent-item:not(.current-org-agent):hover {
	background-color: #f0f0f0;
}

.agent-error {
	display: block;
	width: 100%;
	min-width: 0;
	white-space: normal;
	overflow-wrap: anywhere;
	word-break: break-word;
}

.copy-icon {
	cursor: pointer;
	opacity: 0.7;
}

.copy-icon:hover {
	opacity: 1;
}

.query-section {
	background-color: #f5f5f5;
	border-radius: 4px;
	padding: 8px;
	width: 100%;
}

.query-content {
	background-color: #ffffff;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 8px;
	overflow-x: auto;
	white-space: pre-wrap;
	word-break: break-all;
	max-height: 200px;
	overflow-y: auto;
}

.other-agents-header {
	cursor: pointer;
	padding: 8px;
	border-radius: 4px;
	transition: background-color 0.2s;
}

.other-agents-header:hover {
	background-color: #f5f5f5;
}
</style>
